import { useUpdateTask } from "./useUpdateTask";
import { Task } from "../models/entities/task.model";

export interface UseResolveWorklistAlertResult {
	resolve: (alert: Task) => Promise<void>;
	isPending: boolean;
}

/**
 * Resolve an alert: mark it resolved, refresh the shared task query keys.
 *
 * Same mutation and payload as the Requires attention table's resolve
 * (Maria's answer #5): one click, no confirm step.
 *
 * The row is taken out of the cache BEFORE the request goes out
 * (Maria, 2026-09-29: it has to disappear right away, as reminders do), and
 * put back if the server rejects the resolve. `useUpdateTask` invalidates
 * both shared task keys (list AND count) on success, so the refetch confirms
 * the removal and Requires attention catches up. There is deliberately no
 * second invalidate: a second one cancelled and restarted the slow
 * list refetch, roughly doubling how long the resolved row stayed visible.
 */
export const useResolveWorklistAlert = (
	hideAlert: (alertId: number) => Promise<() => void>,
	resolvedByUserId: number | undefined
): UseResolveWorklistAlertResult => {
	const updateMutation = useUpdateTask();

	const resolve = async (alert: Task) => {
		const restore = await hideAlert(alert.id);
		updateMutation.mutate(
			{
				id: alert.id,
				body: {
					resolved: true,
					participantId: alert.participantId,
					resolutionSource: "MANUAL",
					resolvedByUserId,
				},
			},
			{ onError: () => restore() }
		);
	};

	return {
		resolve,
		isPending: updateMutation.isPending,
	};
};
